import React from 'react';
import _ from 'lodash';

import {
  Box,
  Button,
  Divider,
  Drawer,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  Form,
  errorUtil,
  fields,
  formUtil,
  modalUtil
} from 'iron-forge-react-modules';

import { useForm } from 'react-hook-form';
import * as dataService from './dataService';
import * as types from './types';
import { SelectItemsModal } from 'modules';
import * as UpdateTemplatePriceModal from './UpdateTemplatePriceModal/UpdateTemplatePriceModal';
import Big from 'big.js';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();



export interface IProps {
  templateId: string;
  onClose: () => void;
}

export const FC: React.FC<IProps> = ({ templateId, onClose }) => {
  const queryTuple = dataService.useQuery(templateId);
  const [
    saveTemplateGroupName,
    saveTemplateGroupNameTuple
  ] = dataService.useSaveListingManagementStrategyTemplateGroupName(templateId);
  const [
    saveTemplateGroupTemplates,
    saveTemplateGroupTemplatesTuple
  ] = dataService.useSaveListingManagementStrategyTemplateGroupTemplates(templateId);
  const form = useForm<types.IFormState>({
    defaultValues: {
      name: ''
    }
  });
  formUtil.useSetFormFields(queryTuple, form.setValue, ['name']);
  const {
    modalType2IsOpen,
    modalType2TargetId,
    setModalIsOpen
  } = modalUtil.useModalTrackers(['selectItems', 'updateTemplatePrice']);



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      await errorUtil.errorWrap(async () => {
        await saveTemplateGroupName(formState);
      });
    },
    [saveTemplateGroupName]
  );



  const selectedItemIds = React.useMemo(
    () => queryTuple.data.templates.map(template => template.itemId),
    [queryTuple]
  );

  const loading = queryTuple.loading || saveTemplateGroupTemplatesTuple.loading;
  return (
    <>
      <Drawer
        open
        anchor="right"
        onClose={onClose}>
        <Box
          px={2}
          minWidth="35vw">
          <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
            <Typography variant="h1">
              Template
            </Typography>

            <Stack
              direction="row"
              justifyContent="stretch"
              alignItems="center"
              sx={{ my: 2 }}
              gap={1}>
              {queryTuple.loading ? <Skeleton sx={{ flex: 1 }} /> : (
                <ControlledTextField
                  disableDefaultMargins
                  fieldKey="name"
                  label="Name"
                  isRequired
                  formControl={form.control}
                  isDisabled={saveTemplateGroupNameTuple.loading} />
              )}

              {queryTuple.loading ? <Skeleton sx={{ minWidth: 100 }} /> : (
                <Button
                  type="submit"
                  disabled={saveTemplateGroupNameTuple.loading}>
                  Save
                </Button>
              )}
            </Stack>

            <Divider />

            <Stack
              direction="row"
              alignItems="center"
              gap={2}>
              <Typography variant="h2">
                Items
              </Typography>

              <Button
                disabled={loading}
                onClick={() => setModalIsOpen('selectItems', true)}>
                Add Items
              </Button>
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableCell>Name</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Price Dollars</TableCell>
                </TableHead>

                <TableBody>
                  {queryTuple.loading && _.times(10).map(i => (
                    <TableRow key={i}>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                    </TableRow>
                  ))}

                  {!queryTuple.loading && queryTuple.data.templates.map(template => (
                    <TableRow key={template.id}>
                      <TableCell>
                        {template.itemName}
                      </TableCell>

                      <TableCell>
                        {template.itemSku}
                      </TableCell>

                      <TableCell>
                        <Button
                          disabled={loading}
                          size="small"
                          onClick={() => setModalIsOpen('updateTemplatePrice', template.id)}>
                          {template.priceDollars.toFixed(2)}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Form.FC>
        </Box>
      </Drawer>

      {modalType2IsOpen.selectItems && (
        <SelectItemsModal.FC
          selectedItemIds={selectedItemIds}
          onClose={() => setModalIsOpen('selectItems', false)}
          onSubmit={async items => {
            await errorUtil.errorWrap(async () => {
              setModalIsOpen('selectItems', false);
              await saveTemplateGroupTemplates(items);
            });
          }} />
      )}

      {modalType2TargetId.updateTemplatePrice && (
        <UpdateTemplatePriceModal.FC
          value={queryTuple.data.templates.find(template => template.id === modalType2TargetId.updateTemplatePrice)?.priceDollars ?? new Big(0)}
          onChange={async value => {
            await errorUtil.errorWrap(async () => {
              const templates = _.map(queryTuple.data.templates, template => ({
                itemId: template.itemId,
                defaultPriceDollars: template.id === modalType2TargetId.updateTemplatePrice ? value : template.priceDollars
              }));
              setModalIsOpen('updateTemplatePrice', false);
              await saveTemplateGroupTemplates(templates);
            });
          }}
          onClose={() => setModalIsOpen('updateTemplatePrice', false)} />
      )}
    </>
  );
};
